import Phaser from 'phaser';
import { COLORS, COLORS_HEX, UI, DEPTH } from '../constants';

export interface UIButtonConfig {
  x: number;
  y: number;
  width?: number;
  height?: number;
  text: string;
  fontSize?: number;
  onClick?: () => void;
  disabled?: boolean;
}

export class UIButton extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Rectangle;
  private label: Phaser.GameObjects.Text;
  private isDisabled: boolean;
  private isHovered: boolean = false;
  private isSelected: boolean = false;
  private onClickCallback?: () => void;

  constructor(scene: Phaser.Scene, config: UIButtonConfig) {
    super(scene, config.x, config.y);

    const height = config.height ?? UI.BUTTON_HEIGHT;
    const fontSize = config.fontSize ?? UI.FONT_SIZE_MEDIUM;
    this.isDisabled = config.disabled ?? false;
    this.onClickCallback = config.onClick;

    // Label first so we can measure text width for dynamic button width
    this.label = scene.add.text(0, 0, config.text, {
      fontSize: `${fontSize}px`,
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.label.setOrigin(0.5);

    const minWidth = config.width ?? UI.BUTTON_WIDTH;
    const textWidth = this.label.width;
    const width = Math.max(minWidth, textWidth + UI.BUTTON_PADDING * 2);

    // Background (transparent by default; selected state uses fill)
    this.background = scene.add.rectangle(0, 0, width, height, COLORS.PRIMARY_LIGHT, 0);
    this.background.setStrokeStyle(2, COLORS.PRIMARY_LIGHT);

    this.add([this.background, this.label]);
    this.setSize(width, height);
    this.setDepth(DEPTH.UI_ELEMENTS);

    // Make interactive
    this.background.setInteractive({ useHandCursor: !this.isDisabled });

    // Input events
    this.background.on('pointerover', () => this.onPointerOver());
    this.background.on('pointerout', () => this.onPointerOut());
    this.background.on('pointerdown', () => this.onPointerDown());
    this.background.on('pointerup', () => this.onPointerUp());

    this.updateVisuals();

    scene.add.existing(this);
  }

  private onPointerOver(): void {
    if (this.isDisabled) return;
    this.isHovered = true;
    this.updateVisuals();
  }

  private onPointerOut(): void {
    this.isHovered = false;
    this.updateVisuals();
  }

  private onPointerDown(): void {
    if (this.isDisabled) return;
    if (this.isSelected) {
      this.background.setFillStyle(COLORS.PRIMARY_DARK, 1);
    } else {
      this.background.setFillStyle(COLORS.PRIMARY_LIGHT, 0.2);
    }
  }

  private onPointerUp(): void {
    if (this.isDisabled) return;
    this.updateVisuals();
    if (this.onClickCallback) {
      this.onClickCallback();
    }
  }

  private updateVisuals(): void {
    if (this.isDisabled) {
      this.background.setFillStyle(COLORS.BG_MEDIUM, 0.3);
      this.background.setStrokeStyle(2, COLORS.BG_LIGHT);
      this.label.setColor('#666666');
    } else if (this.isSelected) {
      this.background.setFillStyle(COLORS.PRIMARY, 1);
      this.background.setStrokeStyle(2, COLORS.PRIMARY);
      this.label.setColor(COLORS_HEX.DEEP_AMBER);
    } else if (this.isHovered) {
      this.background.setFillStyle(COLORS.PRIMARY_LIGHT, 0);
      this.background.setStrokeStyle(2, COLORS.PRIMARY_LIGHT);
      this.label.setColor(COLORS_HEX.NEON_ICE);
    } else {
      this.background.setFillStyle(COLORS.PRIMARY_LIGHT, 0);
      this.background.setStrokeStyle(2, COLORS.PRIMARY_LIGHT);
      this.label.setColor(COLORS_HEX.NEON_ICE);
    }
  }

  // Selection for gamepad/keyboard navigation
  setSelected(selected: boolean): void {
    this.isSelected = selected;
    this.updateVisuals();
  }

  isSelectedState(): boolean {
    return this.isSelected;
  }

  // Programmatic click (for gamepad/keyboard)
  click(): void {
    if (!this.isDisabled && this.onClickCallback) {
      this.onClickCallback();
    }
  }

  // Update button text (resizes button to fit if text is longer)
  setText(text: string): void {
    this.label.setText(text);
    const minWidth = UI.BUTTON_WIDTH;
    const textWidth = this.label.width;
    const width = Math.max(minWidth, textWidth + UI.BUTTON_PADDING * 2);
    this.background.setSize(width, this.background.height);
    this.setSize(width, this.height);
  }

  // Enable/disable
  setDisabled(disabled: boolean): void {
    this.isDisabled = disabled;
    this.background.input!.enabled = !disabled;
    this.background.input!.cursor = disabled ? 'default' : 'pointer';
    this.updateVisuals();
  }

  // Alias for setDisabled
  setEnabled(enabled: boolean): void {
    this.setDisabled(!enabled);
  }

  // Set callback
  setOnClick(callback: () => void): void {
    this.onClickCallback = callback;
  }
}
